import { ITEM_TYPES } from 'constants/itemType';
import { NotificationPriority } from 'hooks/useNotification';
import type { UserNotificationWithDetails } from 'hooks/useUserNotifications';
import {
  assetsInCone,
  findUscgFocSeAsset,
  formatUscgFocAssetLabel,
  USCG_FOC_SE_ADVISORIES,
  USCG_FOC_SE_ASSETS,
  type UscgFocDemoAsset,
} from 'components/MapPage/CorporateViewTabs/LayersTab/foc-demo/uscgFocSoutheastTropicalData';

/** Stable id for the hardcoded USCG FOC Southeast tropical inbox item. */
export const USCG_FOC_SE_THREAT_NOTIFICATION_ID = 'demo-uscg-foc-se-tropical';

export const USCG_FOC_SE_ORG_EVENT_DEMO_KEY = 'uscg-foc-se-tropical';

const USCG_FOC_SE_THREAT_SENT_AT = '2024-09-25T14:00:00.000Z';

const TS_ADVISORY =
  USCG_FOC_SE_ADVISORIES.find((a) => a.category === 'TS') ??
  USCG_FOC_SE_ADVISORIES[1];

/** Equinor-style Source line: count + threat + feed name. */
export const USCG_FOC_SE_THREAT_PROVENANCE = `${assetsInCone().length} Assets Impacted by Tropical Storm Helene per Active Hurricanes / Tropical Cyclones (Global)`;

export const USCG_FOC_SE_THRESHOLD_DESCRIPTION =
  'Tropical storm category threshold exceeded — Helene forecast cone contains USCG Southeast District assets';

export const USCG_FOC_SE_EVENT_SUMMARY =
  'Tropical Storm Helene is tracking toward the Southeast District. The National Hurricane Center forecast cone covers Florida–Georgia–South Carolina coastal sectors; boats, aircraft, and cutters face elevated SAR and readiness demand as tropical-storm conditions approach.';

export const USCG_FOC_SE_LINKED_EVENT_NAME =
  'Tropical Storm Helene — USCG Southeast District assets in forecast cone';

export const USCG_FOC_SE_APP_MESSAGE =
  'Tropical Storm Helene threatens USCG Southeast District units. Forecast cone covers Florida–Georgia–South Carolina coasts; sectors and cutters at elevated SAR/readiness risk.';

export type UscgFocSeAssetOperationalStatus =
  | 'Operational'
  | 'Partially Operational'
  | 'Not Operational';

export type UscgFocSeAssetAtRisk = {
  id: string;
  resource: string;
  risk: string;
  location: [number, number];
  operationalStatus: UscgFocSeAssetOperationalStatus;
};

export type UscgFocSeRegionalThreats = {
  region: string;
  description: string;
  threats: UscgFocSeAssetAtRisk[];
  responseChecklist: { id: string; label: string }[];
};

/**
 * Stable checklist labels — also used as org-action names when FOC ensures DB
 * rows. Includes Review ACP & GRS for the FOC response plan story.
 */
export const USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS = [
  {
    id: 'foc-se-checklist-review-acp-grs',
    label: 'Review ACP & GRS',
  },
  {
    id: 'foc-se-checklist-notify-sectors',
    label:
      'Notify Southeast District sectors and subordinate units inside the Helene forecast cone.',
  },
  {
    id: 'foc-se-checklist-stage-sar',
    label:
      'Stage SAR / cutter readiness and confirm OPCON of boats and aircraft for units in the cone.',
  },
  {
    id: 'foc-se-checklist-stand-up-workspace',
    label:
      'Stand up an FOC / IMT workspace for Tropical Storm Helene and track Southeast District readiness.',
  },
] as const;

export const USCG_FOC_SE_RESPONSE_CHECKLIST_LABELS =
  USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS.map((item) => item.label);

export const USCG_FOC_SE_ORG_ACTION_DESCRIPTION =
  'USCG FOC Southeast tropical demo — auto-created from Response Checklist. Planned / Unassigned.';

export function isUscgFocSeThreatOrgActionName(
  name: string | null | undefined
): boolean {
  return (
    !!name &&
    (USCG_FOC_SE_RESPONSE_CHECKLIST_LABELS as readonly string[]).includes(name)
  );
}

/** Notes marker for FOC SE cone resource sets (idempotent ensure). */
export const USCG_FOC_SE_RESOURCE_NOTES_PREFIX = 'uscg-foc-se-asset:';

export function uscgFocSeResourceNotesMarker(assetId: string): string {
  return `${USCG_FOC_SE_RESOURCE_NOTES_PREFIX}${assetId}`;
}

export function uscgFocSeAssetIdFromResourceNotes(
  notes: string | null | undefined
): string | undefined {
  if (!notes?.startsWith(USCG_FOC_SE_RESOURCE_NOTES_PREFIX)) return undefined;
  const id = notes.slice(USCG_FOC_SE_RESOURCE_NOTES_PREFIX.length);
  return id || undefined;
}

export function isUscgFocSeResourceSet(resource: {
  notes?: string | null;
}): boolean {
  const assetId = uscgFocSeAssetIdFromResourceNotes(resource.notes);
  return !!assetId && USCG_FOC_SE_ASSETS.some((asset) => asset.id === assetId);
}

export function findUscgFocSeDemoSite(assetId: string | undefined) {
  return findUscgFocSeAsset(assetId);
}

function buildAssetFromUnit(asset: UscgFocDemoAsset): UscgFocSeAssetAtRisk {
  return {
    id: asset.id,
    resource: formatUscgFocAssetLabel(asset),
    risk:
      `${asset.name} (${asset.city}, ${asset.state}) sits inside the Helene ` +
      `forecast cone while the system is at Tropical Storm intensity ` +
      `(${TS_ADVISORY.maxWindMph} mph). Confirm readiness, review ACP & GRS, and report status to Southeast District.`,
    location: [asset.lon, asset.lat],
    operationalStatus: 'Partially Operational',
  };
}

export function getUscgFocSoutheastRegionalThreats(): UscgFocSeRegionalThreats {
  const inCone = assetsInCone();
  return {
    region: 'USCG Southeast District — Helene forecast cone',
    description:
      'Tropical Storm Helene places the following Coast Guard assets inside the National Hurricane Center forecast cone at elevated risk:',
    threats: inCone.map(buildAssetFromUnit),
    responseChecklist: USCG_FOC_SE_RESPONSE_CHECKLIST_ITEMS.map((item) => ({
      id: item.id,
      label: item.label,
    })),
  };
}

export const USCG_FOC_SE_THREAT_THRESHOLDS = [
  {
    input: 'Storm category',
    region: 'NHC advisory (Helene)',
    threshold: '≥ Tropical Storm (TS)',
    observed: `TS — ${TS_ADVISORY.label} (${TS_ADVISORY.maxWindMph} mph)`,
  },
  {
    input: 'Forecast cone vs USCG assets',
    region: 'Southeast District',
    threshold: '≥ 1 asset in cone',
    observed: `${assetsInCone().length} assets in cone`,
  },
] as const;

export function isHardcodedUscgFocSeThreatNotification(
  notificationId: string | null | undefined
): boolean {
  return notificationId === USCG_FOC_SE_THREAT_NOTIFICATION_ID;
}

export function getUscgFocSoutheastThreatNotification(
  groupId: string
): UserNotificationWithDetails {
  return {
    userGuid: 'system',
    notificationId: USCG_FOC_SE_THREAT_NOTIFICATION_ID,
    groupId,
    archived: false,
    acknowledged: false,
    canArchive: false,
    createdAt: USCG_FOC_SE_THREAT_SENT_AT,
    acknowledgedAt: null,
    archivedAt: null,
    priority: NotificationPriority.WARNING,
    status: 'sent',
    acknowledgementRequired: false,
    sourceItemId: '',
    sourceItemType: ITEM_TYPES.Sitrep,
    sentAt: USCG_FOC_SE_THREAT_SENT_AT,
    scheduledDate: null,
    incidentId: null,
    aors: [],
    appMessage: USCG_FOC_SE_APP_MESSAGE,
    emailSubject: null,
    emailMessage: null,
    smsMessage: null,
    teamsMessage: null,
    sendEmail: null,
    sendSms: null,
    sendTeams: null,
    actionType: null,
    createdByName: USCG_FOC_SE_THREAT_PROVENANCE,
  };
}
